import { closedPaywallState, serializePaywallUrl, paywallParsers } from '@/libs/paywall-url-state';
import { CloseButton } from '@/components/ui/close-button';
import { DialogContent, DialogRoot, DialogTitle } from '@/components/ui/dialog';
import { Alert } from '@/components/ui/alert';
import { useAuth } from '@/contexts/auth-context';
import { useReconcileShopifyFreeApproval } from '@/hooks/actions/use-reconcile-shopify-free-approval';
import { isOnFreePlan } from '@/guards/free-plan-access';
import { useBillingContextQuery } from '@/hooks/queries/use-billing-context-query';
import { useBillingSettingsQuery } from '@/hooks/queries/use-billing-settings-query';
import { useBillingOffersQuery } from '@/hooks/queries/use-billing-offers-query';
import { usePaidCheckoutStatusQuery } from '@/hooks/queries/use-paid-checkout-status-query';
import { useShopifyIntegrationQuery } from '@/hooks/queries/use-integrations-query';
import { usePlansQuery } from '@/hooks/queries/use-plans-query';
import { useShopCurrency } from '@/hooks/queries/use-shop-currency';
import { useSubscriptionQuery } from '@/hooks/queries/use-subscription-query';
import { useOpenBillingPortal } from '@/hooks/actions/use-open-billing-portal';
import { PLAN_IDS } from '@/lib/payload';
import { formatPlanPrice } from '@/libs/utils/format-plan-price';
import { formatCents } from '@/libs/utils/format-money';
import { isPaidCheckoutPending, readBillingContext } from '@/libs/billing-access';
import { getMcpReturnTo } from '@/libs/mcp-return-to';
import { PlanTier, type OrganizationBillingContext } from '@fluenceur/types';
import { formatSettingsDate } from '@/pages/settings/components/format-settings-date';
import { PlanTermsAcceptance } from '@/pages/paywall/_components/PlanTermsAcceptance';
import { usePlanCommitment } from '@/pages/paywall/_components/use-plan-commitment';
import { Box, Button, Flex, Grid, Skeleton, Stack, Text } from '@chakra-ui/react';
import { useQueryState } from 'nuqs';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { PlanChoiceCard } from './PlanChoiceCard';
import { PlanChoiceFooter } from './PlanChoiceFooter';
import {
  buildBillingPlanChoices,
  freePlanPrice,
  isActivePaidSubscription,
  readShopifyPlanCatalogue,
  type PlanChoiceId,
} from './plan-choice-offer';
import { PaidCheckoutNotice } from './PaidCheckoutNotice';
import { PlanChangeNotice } from './PlanChangeNotice';
import { PlanChoiceFeeNote } from './PlanChoiceFeeNote';
import { PlanComparison } from './PlanComparison';
import {
  canCheckShopifyFreeApproval,
  shopifyFreeApprovalStatus,
} from './shopify-free-approval-status';

export type PlanChoiceContext = 'onboarding' | 'upgrade';

interface PlanChoicePanelProps {
  isOpen?: boolean;
  onDecided: () => void;
  initialPlan?: PlanChoiceId;
  minimumPlan?: 'essential' | 'growth';
  /** Feature entry chooses an offer before its existing billing setup. */
  onConfirmPaid?: (plan: 'essential' | 'growth') => void;
  context?: PlanChoiceContext;
  /** The selected plan is URL-backed, optionally supplied by the billing host. */
  selectedPlan?: PlanChoiceId;
  onSelectPlan?: (plan: PlanChoiceId) => void;
  onConfirmFree?: () => void;
  catalogCurrency?: string;
  paidAvailable?: boolean;
  paidUnavailableMessage?: string;
  freeBillingSummary?: string;
  billingContext?: OrganizationBillingContext;
  refreshBilling?: () => Promise<unknown>;
  /** False while the organization has no access: no close button, Escape or outside click. */
  dismissible?: boolean;
  /** Ways out shown under the plans when the choice cannot be dismissed. */
  exits?: ReactNode;
  /** Account ways out shown at the top right of the header. */
  headerActions?: ReactNode;
}

/** The same offer, readiness guards and billing decision on both surfaces. */
export const PlanChoicePanel = ({
  isOpen = true,
  onDecided,
  initialPlan = 'free',
  minimumPlan,
  onConfirmPaid,
  context = 'onboarding',
  selectedPlan,
  onSelectPlan,
  onConfirmFree,
  catalogCurrency,
  paidAvailable: suppliedPaidAvailability,
  paidUnavailableMessage,
  freeBillingSummary,
  billingContext: suppliedBillingContext,
  refreshBilling,
  dismissible = true,
  exits,
  headerActions,
}: PlanChoicePanelProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const brandId = user?.currentBrandId ?? null;
  const billing = useBillingContextQuery({
    enabled: isOpen && !!brandId && !suppliedBillingContext,
  });
  const candidate = suppliedBillingContext ?? billing.data;
  const billingContext =
    candidate &&
    readBillingContext(candidate) &&
    candidate.brandId === brandId &&
    candidate.organizationId === user?.currentBrand?.organizationId
      ? candidate
      : undefined;
  const isStripe = billingContext?.channel === 'stripe';
  // A brand arriving from its store reads the plan choice in the words of a Shopify app.
  const isShopify = billingContext?.channel === 'shopify';
  const billingSettings = useBillingSettingsQuery({
    context: billingContext,
    enabled: isOpen && isStripe,
    pollPaymentRecovery: true,
  });
  const settingsSnapshot =
    billingSettings.data?.organizationId === billingContext?.organizationId &&
    billingSettings.data?.brandId === billingContext?.brandId
      ? billingSettings.data
      : undefined;
  const paymentRecovery = settingsSnapshot?.paymentRecovery;
  const hasDisputeDebt = !!paymentRecovery?.disputes?.length;
  const hasKnownDebt = !!paymentRecovery && (!!paymentRecovery.invoices.length || hasDisputeDebt);
  const billingPortal = useOpenBillingPortal(settingsSnapshot);
  const billingError = billingSettings.isFetching || !!billingSettings.error;
  const canOpenRecoveryPortal =
    hasKnownDebt &&
    !!settingsSnapshot?.canManage &&
    settingsSnapshot.portalAvailable &&
    (!hasDisputeDebt || !!paymentRecovery?.invoices.length) &&
    !billingError;
  const needsLegacyShopify =
    isOpen &&
    billingContext?.channel === 'shopify' &&
    !readShopifyPlanCatalogue(billingContext, undefined).isCanonicalFree;
  const shopCurrency = useShopCurrency({ enabled: needsLegacyShopify });
  const {
    isCanonicalFree: canonicalShopifyFree,
    currency: shopifyCatalogueCurrency,
    unavailable: carrierCatalogueUnavailable,
  } = readShopifyPlanCatalogue(billingContext, shopCurrency);
  const offers = useBillingOffersQuery({ context: billingContext, enabled: isOpen && isStripe });
  const returnTo =
    getMcpReturnTo(serializePaywallUrl(location.pathname + location.search, closedPaywallState)) ??
    getMcpReturnTo(new URLSearchParams(location.search).get('returnTo'));
  const paidStatus = usePaidCheckoutStatusQuery({
    context: billingContext,
    enabled: isOpen && !suppliedBillingContext,
    ...(returnTo ? { returnTo } : {}),
  });
  const subscription = useSubscriptionQuery({
    enabled: needsLegacyShopify && !!brandId,
    scopeKey: brandId,
  });
  const integration = useShopifyIntegrationQuery({ enabled: needsLegacyShopify });
  const plans = usePlansQuery({
    currency: isStripe
      ? (billingContext?.billingCurrency ?? undefined)
      : billingContext?.channel === 'shopify'
        ? shopifyCatalogueCurrency
        : catalogCurrency,
    enabled: isOpen,
  });
  const choices = buildBillingPlanChoices(
    plans.data?.plans,
    billingContext,
    isStripe ? offers.data : undefined
  );
  const [urlSelected, setUrlSelected] = useQueryState(
    'plan',
    paywallParsers.plan.withDefault(initialPlan).withOptions({ history: 'push', shallow: false })
  );
  const selected = selectedPlan ?? urlSelected;
  const select =
    onSelectPlan ??
    ((plan: PlanChoiceId) => {
      void setUrlSelected(plan);
    });
  const selectedChoice = choices?.find((choice) => choice.id === selected);
  const currentSubscription =
    isStripe || canonicalShopifyFree ? billingContext?.subscription : subscription.data;
  const hasActiveSubscription = isActivePaidSubscription(currentSubscription);
  const freeApproval = billingContext?.shopifyFreeApproval;
  const needsFreeApproval =
    billingContext?.channel === 'shopify' &&
    !!freeApproval &&
    ['required', 'pending'].includes(freeApproval.requirement);
  const freeApprovalPending =
    needsFreeApproval &&
    ['reserved', 'processing', 'uncertain'].includes(freeApproval?.status ?? '');
  const reconcileFree = useReconcileShopifyFreeApproval();
  const [comparing, setComparing] = useState(false);
  const comparisonId = useId();
  const currentPlanId = hasActiveSubscription ? currentSubscription?.planId : null;
  const selectedBillingId =
    selected === 'free' ? null : PLAN_IDS[selected.toUpperCase() as 'ESSENTIAL' | 'GROWTH'];
  const restoringScheduledPlan =
    isStripe &&
    billingContext?.planChange?.kind === 'scheduled' &&
    selectedBillingId === currentPlanId;
  const isCurrentPlan =
    selectedBillingId !== null && selectedBillingId === currentPlanId && !restoringScheduledPlan;
  const unavailableFree =
    selected === 'free' && !hasActiveSubscription && !canonicalShopifyFree && !isOnFreePlan(user);
  const ready =
    !!brandId &&
    !!billingContext &&
    !!choices &&
    (isStripe || canonicalShopifyFree || subscription.data !== undefined) &&
    !plans.isPending &&
    !plans.isFetching &&
    !plans.error &&
    (suppliedBillingContext || (!billing.isPending && !billing.isFetching && !billing.error)) &&
    (isStripe ||
      canonicalShopifyFree ||
      (!subscription.isPending &&
        !subscription.isFetching &&
        !subscription.error &&
        !integration.isPending &&
        !integration.isFetching &&
        !integration.error));
  const pendingSubscription = currentSubscription?.status === 'pending' && !needsFreeApproval;
  const pendingChange = !isStripe ? currentSubscription?.pendingChange : null;
  const pendingChangeDate = formatSettingsDate(pendingChange?.effectiveDate, t('common.locale'));
  const hasPendingChangeDate = pendingChangeDate !== '-';
  const paidOperation =
    billingContext?.paidCheckout?.status === 'expired' ? null : billingContext?.paidCheckout;
  const paidAvailable =
    !!billingContext?.capabilities.canCommitPaidPlan &&
    billingContext.cardSetup.exemption !== 'impersonation' &&
    (billingContext.channel === 'shopify' ||
      (isStripe &&
        (hasActiveSubscription || billingContext.checkoutAvailability.paid) &&
        billingContext.taxQualificationRequired === false &&
        !!billingContext.billingCurrency &&
        !offers.isPending &&
        !offers.isFetching &&
        !offers.error &&
        offers.data?.currency === billingContext.billingCurrency &&
        !!offers.data?.offers.length)) &&
    !carrierCatalogueUnavailable &&
    suppliedPaidAvailability !== false;
  const {
    commit,
    isCommitting,
    planAwaitingTerms,
    dismissTerms,
    resumeAfterAcceptance,
    chargeResult,
    planChangeResult,
    checkPlanChange,
    isCheckingPlanChange,
    isPlanChangePending,
  } = usePlanCommitment({
    isChangeMode: hasActiveSubscription,
    hasShopifyIntegration: !!integration.data || canonicalShopifyFree,
    isCheckingIntegration: !ready || pendingSubscription || !!pendingChange,
    onMissingIntegration: () => navigate('/onboarding/shopify'),
    billingContext,
    ...(returnTo ? { returnTo } : {}),
  });
  const busy =
    isCommitting(PLAN_IDS.ESSENTIAL) || isCommitting(PLAN_IDS.GROWTH) || isCommitting('free');
  const paidPending = isPaidCheckoutPending(billingContext, chargeResult);
  const awaitingBilling = pendingSubscription || !!pendingChange || paidPending;
  const freeReceiptPending =
    chargeResult?.kind === 'pending' &&
    chargeResult.planId === 'free' &&
    !freeApproval?.operationId;
  const meetsMinimum = (plan: PlanChoiceId) =>
    !minimumPlan || (plan !== 'free' && (minimumPlan !== 'growth' || plan === 'growth'));
  const canPreparePaidDetails =
    !!onConfirmPaid &&
    isStripe &&
    !!billingContext?.capabilities.canCommitPaidPlan &&
    billingContext.cardSetup.exemption !== 'impersonation' &&
    billingContext.checkoutAvailability.freeCardSetup &&
    billingContext.taxQualificationRequired === true &&
    (billingContext.effectiveTier === PlanTier.FREE ||
      billingContext.effectiveTier === PlanTier.FREE_BETA) &&
    currentSubscription === null &&
    !!selectedChoice?.price &&
    !selectedChoice.indicative &&
    !offers.isPending &&
    !offers.isFetching &&
    !offers.error;
  const blocked =
    !meetsMinimum(selected) ||
    !ready ||
    busy ||
    awaitingBilling ||
    isPlanChangePending ||
    isCurrentPlan ||
    unavailableFree ||
    (selected === 'free' &&
      needsFreeApproval &&
      (!freeApproval?.canApprove || freeApprovalPending || freeReceiptPending)) ||
    (selected !== 'free' && (freeApproval?.requirement === 'pending' || freeReceiptPending)) ||
    (selected !== 'free' && ((!paidAvailable && !canPreparePaidDetails) || hasKnownDebt));
  const confirmLabel = restoringScheduledPlan
    ? t('planChange.scheduled.keepCurrent', { plan: t(`pricing.plan.${selected}.name`) })
    : isCurrentPlan
      ? t('pricing.cta.currentPlan')
      : selected === 'free'
        ? needsFreeApproval
          ? t(
              freeApproval?.status === 'pending' && freeApproval.approvalUrl
                ? 'shopifyFreeApproval.resume'
                : 'shopifyFreeApproval.approve'
            )
          : t(
              hasActiveSubscription
                ? 'settings.subscription.switchToFree.button'
                : 'planChoice.continueFree'
            )
        : selectedChoice?.price && !selectedChoice.indicative
          ? t('planChoice.upgrade', {
              plan: t(`pricing.plan.${selected}.name`),
              price: formatPlanPrice(
                selectedChoice.price.amount,
                selectedChoice.price.currencyCode,
                t('common.locale')
              ),
            })
          : t('pricing.cta.upgradeTo', { plan: t(`pricing.plan.${selected}.name`) });
  const confirm = () => {
    if (blocked) return;
    if (selected !== 'free' && onConfirmPaid) {
      onConfirmPaid(selected);
      return;
    }
    if (selected === 'free') {
      if (needsFreeApproval) {
        commit('free');
        return;
      }
      (onConfirmFree ?? onDecided)();
      if (hasActiveSubscription) navigate('/settings/billing');
      return;
    }
    if (selectedBillingId) {
      if (
        isStripe &&
        (billingContext?.cardSetup.identityRequired ||
          billingContext?.cardSetup.emailVerificationRequired)
      ) {
        navigate(
          serializePaywallUrl(location.pathname + location.search + location.hash, {
            paywall: 'plans',
            plan: selected,
          })
        );
        return;
      }
      commit(selectedBillingId);
    }
  };
  const titleKey =
    context === 'upgrade'
      ? 'pricing.manageTitle'
      : isShopify
        ? 'planChoice.shopify.title'
        : 'planChoice.title';
  const freePrice = freePlanPrice(choices);
  const shopifyFreeTitle = freePrice
    ? t('shopifyFreeApproval.billingTitleWithPrice', {
        price: formatPlanPrice(freePrice.amount, freePrice.currencyCode, t('common.locale')),
      })
    : t('shopifyFreeApproval.billingTitle');
  const readError =
    !!plans.error ||
    !!billing.error ||
    !!paidStatus.error ||
    (isStripe
      ? !!offers.error
      : !canonicalShopifyFree && (!!subscription.error || !!integration.error)) ||
    (!billingContext && !billing.isPending) ||
    (!choices && !plans.isPending);
  const showsFreeApproval = selected === 'free' && needsFreeApproval && !!freeApproval;
  const freeApprovalStatus =
    showsFreeApproval && freeApproval
      ? shopifyFreeApprovalStatus(freeApproval, freeReceiptPending)
      : null;
  const checkFreeApproval = () => {
    if (!freeApproval?.canApprove) return;
    if (!freeApproval.operationId) {
      void (refreshBilling ? refreshBilling() : billing.refetch());
      return;
    }
    void reconcileFree
      .mutateAsync(freeApproval.operationId)
      .then(() => (refreshBilling ? refreshBilling() : billing.refetch()))
      .catch(() => undefined);
  };
  const choiceCurrency =
    billingContext?.channel === 'shopify'
      ? shopifyCatalogueCurrency
      : billingContext?.billingCurrency;
  const contents = (
    <>
      <Flex
        data-testid="plan-choice-header"
        position="relative"
        flexShrink={0}
        align="flex-start"
        justify="space-between"
        gap="12px"
        wrap="wrap"
        bg="lilac.300"
        ps={{ base: '20px', md: '32px' }}
        pe={dismissible ? { base: '64px', md: '72px' } : { base: '20px', md: '32px' }}
        py="20px"
      >
        {dismissible && (
          <CloseButton
            position="absolute"
            top="18px"
            right="20px"
            borderColor="rgba(26, 26, 26, 0.18)"
            color="ink"
            aria-label={t('common.close')}
            onClick={onDecided}
            disabled={busy}
          />
        )}
        <Stack gap="4px" minW={0} flex="1 1 320px">
          <DialogTitle
            fontFamily="heading"
            fontSize={{ base: '22px', md: '26px' }}
            fontWeight="600"
            letterSpacing="-0.02em"
            lineHeight="1.15"
            color="ink"
          >
            {t(titleKey)}
          </DialogTitle>
          <Text fontSize="14px" lineHeight="1.5" color="navInactive">
            {t(isShopify ? 'planChoice.shopify.subtitle' : 'planChoice.subtitle')}
          </Text>
        </Stack>
        {headerActions}
      </Flex>
      <Box px={{ base: '20px', md: '32px' }} py="20px" flex="1" minH={0} overflowY="auto">
        {planChangeResult && (
          <Box mb="16px">
            <PlanChangeNotice
              result={planChangeResult}
              onCheck={checkPlanChange}
              busy={isCheckingPlanChange}
            />
          </Box>
        )}
        {selected !== 'free' && !paidAvailable && !hasKnownDebt && (
          <Alert status="info" mb="16px">
            {billingContext && !billingContext.capabilities.canCommitPaidPlan
              ? t('planChoice.paid.memberBody')
              : (paidUnavailableMessage ?? t('onboarding.billing.paidUnavailable'))}
          </Alert>
        )}
        {selected !== 'free' && hasKnownDebt && paymentRecovery && (
          <Alert status="warning" title={t('settings.subscription.paymentFailure.title')} mb="16px">
            <Stack gap={2}>
              <Text>
                {t(
                  hasDisputeDebt
                    ? settingsSnapshot?.canManage
                      ? 'settings.subscription.paymentRecovery.disputeDescription'
                      : 'settings.subscription.paymentRecovery.disputeMember'
                    : paymentRecovery.status === 'retrying'
                      ? settingsSnapshot?.canManage
                        ? 'settings.subscription.paymentRecovery.retrying'
                        : 'settings.subscription.paymentRecovery.retryingMember'
                      : settingsSnapshot?.canManage
                        ? 'settings.subscription.paymentFailure.stripeDescription'
                        : 'settings.subscription.paymentRecovery.unpaidMember'
                )}
              </Text>
              {paymentRecovery.invoices.map((invoice) => (
                <Text key={invoice.id}>
                  {t(
                    invoice.number
                      ? 'settings.subscription.paymentRecovery.invoice'
                      : 'settings.subscription.paymentRecovery.invoiceWithoutNumber',
                    {
                      number: invoice.number,
                      amount: formatCents(
                        invoice.amountRemaining,
                        invoice.currency,
                        t('common.locale')
                      ),
                      currency: invoice.currency,
                    }
                  )}
                </Text>
              ))}
              {canOpenRecoveryPortal && (
                <Button
                  alignSelf="flex-start"
                  variant="outline"
                  size="sm"
                  loading={billingPortal.isPending}
                  disabled={billingError || billingPortal.isPending}
                  onClick={() => void billingPortal.mutateAsync().catch(() => undefined)}
                >
                  {t('settings.subscription.paymentFailure.stripeAction')}
                </Button>
              )}
            </Stack>
          </Alert>
        )}
        {paidPending && (
          <Box mb="16px">
            <PaidCheckoutNotice
              operation={paidOperation}
              busy={busy}
              canResume={paidAvailable && !hasKnownDebt}
              onCheck={() =>
                void (refreshBilling
                  ? refreshBilling()
                  : paidStatus.canReadOperation
                    ? paidStatus.refetch()
                    : billing.refetch())
              }
              onResume={paidOperation ? () => commit(paidOperation.planId) : undefined}
            />
          </Box>
        )}
        {selected !== 'free' &&
          isStripe &&
          billingContext?.cardSetup.requirement === 'confirmed' &&
          billingContext.cardSetup.exemption === null && (
            <Text mb="16px" fontSize="14px">
              {t('planChoice.paid.savedCard')}
            </Text>
          )}
        {pendingSubscription && (
          <Alert status="info" mb="16px">
            {t('planChoice.pending')}
          </Alert>
        )}
        {pendingChange && (
          <Alert status="info" title={t('pricing.pendingChange.title')} mb="16px">
            {t(
              hasPendingChangeDate
                ? 'pricing.pendingChange.message'
                : 'pricing.pendingChange.messageWithoutDate',
              {
                plan: t(
                  pendingChange.newPlan === PLAN_IDS.GROWTH
                    ? 'pricing.plan.growth.name'
                    : 'pricing.plan.essential.name'
                ),
                ...(hasPendingChangeDate ? { date: pendingChangeDate } : {}),
              }
            )}
          </Alert>
        )}
        {unavailableFree && (
          <Alert status="info" mb="16px">
            {t('planChoice.freeUnavailable')}
          </Alert>
        )}
        {readError && (
          <Flex direction="column" align="center" gap="10px" py="24px">
            <Text fontSize="14px" color="inkMuted">
              {t('planChoice.loadError')}
            </Text>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void plans.refetch();
                void subscription.refetch();
                void integration.refetch();
                void billing.refetch();
                if (isStripe) void offers.refetch();
              }}
            >
              {t('common.retry')}
            </Button>
          </Flex>
        )}
        {choices ? (
          <Grid
            role="radiogroup"
            aria-label={t(titleKey)}
            templateColumns={{ base: '1fr', md: 'repeat(3, minmax(0, 1fr))' }}
            gap="12px"
          >
            {choices.map((choice) => (
              <PlanChoiceCard
                key={choice.id}
                choice={choice}
                checked={choice.id === selected}
                current={
                  choice.id !== 'free' &&
                  PLAN_IDS[choice.id.toUpperCase() as 'ESSENTIAL' | 'GROWTH'] === currentPlanId
                }
                currentSelectable={
                  isStripe &&
                  billingContext?.planChange?.kind === 'scheduled' &&
                  billingContext.capabilities.canCommitPaidPlan &&
                  billingContext.cardSetup.exemption !== 'impersonation'
                }
                disabled={busy || !meetsMinimum(choice.id)}
                onSelect={() => select(choice.id)}
              />
            ))}
          </Grid>
        ) : (
          !readError && (
            <Grid templateColumns={{ base: '1fr', md: 'repeat(3, minmax(0, 1fr))' }} gap="12px">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} h="280px" borderRadius="16px" />
              ))}
            </Grid>
          )
        )}
        {choices && (
          <>
            <Flex
              mt="12px"
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'flex-start', md: 'baseline' }}
              justify="space-between"
              gap={{ base: '6px', md: '16px' }}
            >
              <PlanChoiceFeeNote
                choices={choices}
                currencyCode={choiceCurrency}
                fontSize="12.5px"
                lineHeight="1.45"
                color="inkMuted"
              />
              <Button
                flex="none"
                variant="plain"
                size="sm"
                h="auto"
                p={0}
                fontSize="13px"
                color="ink"
                textDecoration="underline"
                textUnderlineOffset="3px"
                aria-expanded={comparing}
                aria-controls={comparisonId}
                onClick={() => setComparing((open) => !open)}
              >
                {t(comparing ? 'planChoice.compareAll.hide' : 'planChoice.compareAll.show')}
              </Button>
            </Flex>
            {comparing && (
              <PlanComparison id={comparisonId} choices={choices} currencyCode={choiceCurrency} />
            )}
          </>
        )}
        {exits}
      </Box>
      <PlanChoiceFooter
        billing={
          selected === 'free'
            ? needsFreeApproval
              ? 'shopify'
              : 'free'
            : billingContext?.channel === 'shopify'
              ? 'shopify'
              : isStripe
                ? 'stripe'
                : null
        }
        freeBillingSummary={
          needsFreeApproval ? t('shopifyFreeApproval.summary') : freeBillingSummary
        }
        shopifyFreeTitle={needsFreeApproval ? shopifyFreeTitle : undefined}
        status={
          freeApprovalStatus
            ? {
                text: t(freeApprovalStatus.key),
                tone: freeApprovalStatus.warning ? 'warning' : 'muted',
              }
            : undefined
        }
        secondaryAction={
          showsFreeApproval &&
          freeApproval &&
          canCheckShopifyFreeApproval(freeApproval, freeReceiptPending) ? (
            <Button
              variant="plain"
              size="sm"
              h="auto"
              p={0}
              fontSize="13px"
              color="ink"
              textDecoration="underline"
              textUnderlineOffset="3px"
              loading={reconcileFree.isPending}
              onClick={checkFreeApproval}
            >
              {t('shopifyFreeApproval.check')}
            </Button>
          ) : undefined
        }
        confirmLabel={confirmLabel}
        onConfirm={confirm}
        disabled={blocked}
        loading={busy}
      />
    </>
  );

  return (
    <>
      <DialogRoot
        open={isOpen && planAwaitingTerms === null}
        closeOnEscape={dismissible}
        closeOnInteractOutside={dismissible}
        onOpenChange={(details) => {
          if (!details.open && dismissible && !busy && planAwaitingTerms === null) onDecided();
        }}
      >
        <DialogContent
          maxW="1000px"
          w="calc(100vw - 32px)"
          maxH="calc(100dvh - 32px)"
          display="flex"
          flexDirection="column"
          bg="white"
          borderRadius="24px"
          overflow="hidden"
          boxShadow="none"
          p={0}
        >
          {contents}
        </DialogContent>
      </DialogRoot>
      <PlanTermsAcceptance
        isOpen={isOpen && planAwaitingTerms !== null}
        onClose={dismissTerms}
        onAccepted={resumeAfterAcceptance}
      />
    </>
  );
};
